import Vue from 'vue';
import axios from 'axios';
import i18n, { LanguageObject } from '@/i18n';
import { OversightOrganization } from './OversightUtils';
import { ServiceProvider } from './ProviderUtils';
import { UserAgreement } from './UserAgreementUtils';

type Privilege = 'readonly' | 'admin' | 'accompany' | 'sub_org';
type PrivilegeType = 'oversight' | 'serviceprovider' | 'admin' | 'superadmin';
interface Role {
  id?: number;
  name: LanguageObject;
  type?: PrivilegeType;
  permissions: {
    sp_privs?: Privilege[];
    oversight_privs?: Privilege[];
    oversight_children_privs?: Privilege[];
  };
}

interface Session {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  token: string;
  language: string;
  state: string;
  permissions: {
    id: number;
    userid: number;
    role: Partial<Role>;
    oversight: Partial<OversightOrganization>;
    serviceprovider: Partial<ServiceProvider>;
  }[];
  role: Partial<Role> | null;
  useragreements: UserAgreement[];
}

// store session in a separate Vue instance
const SessionVM = new Vue({
  data(): {
    $session?: Session;
  } {
    return {
      $session: undefined,
    };
  },
});

const SessionStore = SessionVM.$data as { $session: Session };

// global mixin to put the computed value $session on every Vue instance
Vue.mixin(
  Vue.extend({
    computed: {
      $session() {
        return SessionVM.$data.$session;
      },
    },
  })
);

let _init: Promise<Session | undefined>;
const init = () => {
  if (!_init) {
    _init = new Promise(async (resolve, reject) => {
      axios.defaults.headers.common['Content-Type'] =
        'application/x-www-form-urlencoded';

      let session = localStorage.session
        ? JSON.parse(localStorage.session)
        : undefined;

      if (session) {
        set(session);
        try {
          session = {
            ...session,
            ...(await get()),
          };
        } catch (error) {
          session = undefined;
        }
      }

      if (session) {
        set(session);
        resolve(session);
      } else {
        clear();
        resolve(undefined);
      }
    });
  }
  return _init;
};

const get = async () => {
  const resp = await axios.post<Session>('/authentication/getUserSession.php');
  return resp.data;
};

const set = (session: Partial<Session>) => {
  const updated = {
    ...SessionVM.$data.$session,
    ...session,
  } as Session;

  // if there is only a single role assigned to the user, automatically select it
  if (!updated.role) {
    const roles = updated?.permissions
      ?.map((permission) => permission.role)
      .filter(
        (role, index, list) =>
          list.findIndex((val) => val.id === role.id) === index
      );
    if (roles.length === 1) {
      updated.role = roles[0];
    }
  }

  localStorage.session = JSON.stringify(updated);

  if (updated.token) {
    axios.defaults.headers.common['X-Custom-Authorization'] = updated.token;
  } else {
    delete axios.defaults.headers.common['X-Custom-Authorization'];
  }

  if (updated.role) {
    axios.defaults.headers.common['X-Custom-Role'] = updated.role.id;
  } else {
    delete axios.defaults.headers.common['X-Custom-Role'];
  }

  if (updated.language) {
    i18n.locale = updated.language;
  }

  SessionVM.$data.$session = updated;
};

const clear = () => {
  localStorage.removeItem('session');
  delete axios.defaults.headers.common['X-Custom-Authorization'];
  delete axios.defaults.headers.common['X-Custom-Role'];
  SessionVM.$data.$session = undefined;
};

const login = async (username: string, password: string) => {
  const resp = await axios.post<Session>('/authentication/login.php', {
    username: username,
    password: password,
  });
  set(resp.data);
  return resp.data;
};

const forgotPassword = async (params: { identity: string }) => {
  await axios.post('/authentication/forgotPassword.php', params);
};

const changePassword = async (params: { token: string; password: string }) => {
  const resp = await axios.post('/authentication/changePassword.php', params);
  set(resp.data);
  return resp.data;
};

export default {
  init,
  get,
  set,
  clear,
  login,
  forgotPassword,
  changePassword,
  SessionStore,
};

export { Session };
